import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"

const log = createLogger('ProhibitedKeywords')

export default class extends Controller {
  static targets = ["titleInput", "titleError", "titleProhibitedError",
                    "descriptionInput", "descriptionError", "descriptionProhibitedError"]
  static values = {
    list: Array,
    minLength: { type: Number, default: 3 }  // Minimum keyword length for matching
  }

  connect() {
    // Track both fields separately to prevent state overwrites
    this.titleHasProhibited = false
    this.descriptionHasProhibited = false
    this.titleMatches = []
    this.descriptionMatches = []
    this.matchedKeywords = []
  }

  checkTitle() {
    this.titleHasProhibited = this.checkField(
      this.titleInputTarget,
      this.titleProhibitedErrorTarget,
      'title'
    )
    this.updateValidationState()
  }

  checkDescription() {
    this.descriptionHasProhibited = this.checkField(
      this.descriptionInputTarget,
      this.descriptionProhibitedErrorTarget,
      'description'
    )
    this.updateValidationState()
  }

  updateValidationState() {
    // ✅ Aggregate both fields - form is invalid if EITHER has prohibited keywords
    const hasProhibitedKeywords = this.titleHasProhibited || this.descriptionHasProhibited

    // Aggregate all matched keywords from both fields
    this.matchedKeywords = [...new Set([...this.titleMatches, ...this.descriptionMatches])]

    // Dispatch event to product-form controller for submit button disabling
    this.dispatch("validation-changed", {
      detail: {
        isValid: !hasProhibitedKeywords,
        matchedKeywords: this.matchedKeywords
      }
    })
  }

  checkField(inputElement, errorElement, fieldName) {
    const text = inputElement.value

    // CRITICAL: Use exact phrase matching (not substring)
    // This prevents false positives like "托盤" triggering on "托"
    const matchedInField = this.listValue.filter(keyword => {
      // Case-insensitive full-word matching for Chinese
      // Remove all spaces and punctuation for comparison
      const cleanText = text.replace(/[\s\.,，。！？!?]/g, '')
      const cleanKeyword = keyword.replace(/[\s]/g, '')

      // Use indexOf for exact phrase match (minLength+ characters only)
      return cleanKeyword.length >= this.minLengthValue &&
             cleanText.toLowerCase().indexOf(cleanKeyword.toLowerCase()) !== -1
    })

    const hasProhibited = matchedInField.length > 0

    // Store matched keywords per field
    if (fieldName === 'title') {
      this.titleMatches = matchedInField
    } else if (fieldName === 'description') {
      this.descriptionMatches = matchedInField
    }

    // Update UI for this specific field
    if (hasProhibited) {
      inputElement.classList.add("border-red-500", "focus:border-red-600")
      inputElement.classList.remove("border-charcoal-300", "focus:border-crust-600")
      errorElement.classList.remove("hidden")
      log.debug(`Prohibited keywords detected in ${fieldName}:`, matchedInField)
    } else {
      inputElement.classList.remove("border-red-500", "focus:border-red-600")
      inputElement.classList.add("border-charcoal-300", "focus:border-crust-600")
      errorElement.classList.add("hidden")
    }

    return hasProhibited
  }

  // Called by product-form controller before submission
  isValid() {
    // Check both fields
    return !this.titleHasProhibited && !this.descriptionHasProhibited
  }

  // Return matched keywords for error messaging
  getMatchedKeywords() {
    return this.matchedKeywords
  }
}
